import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import PersonIcon from '@mui/icons-material/Person';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import NearMeIcon from '@mui/icons-material/NearMe';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import { getLatLong, calcCrow } from '../common/helpers';
import { dinero } from '../../utils/dinero';

// Alto del mapa (px). El placeholder de carga usa el mismo valor para que la página no salte.
const ALTO_MAPA = 438;
const containerStyle = { width: '100%', height: `${ALTO_MAPA}px`, borderRadius: '8px' };
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 }; // Centro de México
// "Cerca de ti": con tu ubicación, el mapa se acerca a los servicios dentro de este radio.
const RADIO_CERCA_KM = 25;
// Tiempo máximo para obtener la ubicación. Antes eran 8 s: el navegador puede estar esperando a que contestes el
// cuadro de "¿Permitir ubicación?" o tardarse en localizarte. Si se agota, el chip deja reintentar con un clic.
const TIEMPO_UBICACION_MS = 20000;
// Tope de acercamiento al encuadrar (con un solo punto, fitBounds llegaría a nivel calle).
const ZOOM_MAX_ENCUADRE = 14;
// Tope de pines en el mapa.
const MAX_PINES = 30;
// Variedad: ningún proveedor ocupa más de estos pines, para que el mapa muestre a distintas
// personas en vez de llenarse con las direcciones de quien tiene más servicios.
const MAX_PINES_POR_PROVEEDOR = 2;
// Direcciones SIN coordenadas guardadas que se geocodifican (cada una es una llamada a Google): las
// primeras de cada proveedor, y un tope total. Las que ya tienen coordenadas no cuentan para esto.
const DIRECCIONES_POR_PROVEEDOR = 5;
const MAX_DIRECCIONES = 40;

// Cache de geocodificación por dirección (persiste durante la sesión)
const geoCache = new Map();

// getLatLong siempre contesta (null si Google no encuentra la dirección, falla o tarda más de 5 s); aquí se recuerda la respuesta
const geocodeSafe = (dir) => {
  if (geoCache.has(dir)) return Promise.resolve(geoCache.get(dir));
  return getLatLong(dir).then(coords => {
    geoCache.set(dir, coords);
    return coords;
  });
};

const mejorRating = (grupo) => Math.max(0, ...grupo.items.map(s => Number(s.rating) || 0));

// Coordenadas guardadas en los servicios de la dirección (GeoJSON [lng, lat]). Si ninguno las
// tiene, el pin se ubica geocodificando la dirección.
const coordsGuardadas = (items) => {
  for (const s of items) {
    const c = s.location?.coordinates;
    if (Array.isArray(c) && c.length === 2 && c.every(Number.isFinite)) return { lat: c[1], lng: c[0] };
  }
  return null;
};

// Conserva los grupos (en su orden) mientras algún proveedor del grupo no haya llegado al tope.
// Un grupo es una dirección y casi siempre es de un solo proveedor.
const limitarPorProveedor = (grupos, tope) => {
  const cuenta = {};
  return grupos.filter(g => {
    const proveedores = [...new Set(g.items.map(s => s.userId || s._id))];
    if (!proveedores.some(p => (cuenta[p] || 0) < tope)) return false;
    proveedores.forEach(p => { cuenta[p] = (cuenta[p] || 0) + 1; });
    return true;
  });
};

const HomeMap = ({ servicios = [], max = MAX_PINES }) => {
  // Los marcadores del mapa van con style (los pinta Google Maps): llevan los valores del tema, no sus nombres
  const { palette } = useTheme();
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [userPos, setUserPos] = useState(null);
  const [mode, setMode] = useState('best'); // 'near' = cerca de ti | 'best' = mejor calificados
  const [buscandoUbicacion, setBuscandoUbicacion] = useState(() => Boolean(navigator.geolocation));
  const [estadoPermiso, setEstadoPermiso] = useState(null); // 'granted' | 'prompt' | 'denied' | null (sin Permissions API)
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));
  // Instancia del mapa en estado (no en ref): GoogleMap llama onLoad un render después de
  // montarse, y así el encuadre se vuelve a ejecutar en cuanto el mapa ya existe.
  const [mapObj, setMapObj] = useState(null);

  // El script de Google Maps se carga en index.html; esperar a que esté listo
  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 300);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Pide la ubicación del usuario (si no la da, userPos queda null y el mapa muestra los mejor calificados).
  const pedirUbicacion = useCallback(() => {
    if (!navigator.geolocation) return;
    setBuscandoUbicacion(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        // Misma posición → mismo objeto, para no recalcular pines si el navegador contesta dos veces
        setUserPos(prev => (prev && prev.lat === lat && prev.lng === lng ? prev : { lat, lng }));
        setBuscandoUbicacion(false);
      },
      () => setBuscandoUbicacion(false),
      { enableHighAccuracy: false, timeout: TIEMPO_UBICACION_MS, maximumAge: 600000 }
    );
  }, []);

  // Se pide al cargar y otra vez cuando el permiso pasa a "permitido" (por ejemplo, desde el candado de la
  // barra de direcciones). Antes era una sola solicitud al cargar: si fallaba, ya no se reintentaba aunque
  // después dieras el permiso, y el mapa se quedaba en "Mejor calificados".
  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    pedirUbicacion();
    let vigente = true;
    let permiso = null;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then(p => {
        if (!vigente) return;
        permiso = p;
        setEstadoPermiso(p.state);
        p.onchange = () => {
          setEstadoPermiso(p.state);
          if (p.state === 'granted') pedirUbicacion();
        };
      }).catch(() => { /* navegador sin Permissions API para geolocalización */ });
    }
    return () => { vigente = false; if (permiso) permiso.onchange = null; };
  }, [pedirUbicacion]);

  // Clave estable: solo cambia cuando cambian las direcciones únicas
  const dirKey = [...new Set(
    servicios.map(s => (s.ubicacion || '').trim()).filter(Boolean)
  )].sort().join('|');

  useEffect(() => {
    let cancelled = false;

    const porDireccion = {};
    servicios.forEach(s => {
      const dir = (s.ubicacion || '').trim();
      if (!dir) return;
      if (!porDireccion[dir]) porDireccion[dir] = [];
      porDireccion[dir].push(s);
    });

    const todas = Object.keys(porDireccion).map(dir => ({
      direccion: dir, items: porDireccion[dir], coords: coordsGuardadas(porDireccion[dir]),
    }));
    // Las direcciones con coordenadas guardadas entran todas (no cuestan nada). Las que hay que
    // geocodificar se limitan a las primeras de cada proveedor (el backend manda primero las mejor
    // calificadas); así el tope total no deja fuera a quien tiene pocos servicios.
    const porGeocodificar = new Set(
      limitarPorProveedor(todas.filter(g => !g.coords), DIRECCIONES_POR_PROVEEDOR).slice(0, MAX_DIRECCIONES)
    );
    const direcciones = todas.filter(g => g.coords || porGeocodificar.has(g));
    if (direcciones.length === 0) {
      setMarkers([]);
      return;
    }

    setLoading(true);
    Promise.all(direcciones.map(async (g) => {
      const coords = g.coords || await geocodeSafe(g.direccion);
      return coords ? { ...g, coords } : null;
    })).then(res => {
      if (cancelled) return;
      let grupos = res.filter(Boolean);

      if (userPos) {
        // Más cercanos al cliente primero
        grupos = grupos
          .map(g => ({ ...g, dist: calcCrow(userPos.lat, userPos.lng, g.coords.lat, g.coords.lng) }))
          .sort((a, b) => a.dist - b.dist);
        setMode('near');
      } else {
        // Sin geolocalización: mejor calificados (el backend ya viene ordenado por rating)
        grupos = grupos.sort((a, b) => mejorRating(b) - mejorRating(a));
        setMode('best');
      }

      // Ya ordenados (más cercanos o mejor calificados primero): máximo MAX_PINES_POR_PROVEEDOR por proveedor.
      setMarkers(limitarPorProveedor(grupos, MAX_PINES_POR_PROVEEDOR).slice(0, max));
      setActive(null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [dirKey, userPos, max]);

  // Encuadre. Con tu ubicación: acerca a los servicios que tienes a menos de RADIO_CERCA_KM
  // (si no hay ninguno, a ti + el más cercano). Sin ubicación: encuadra todos los pines.
  // También lo usa el chip "Cerca de ti" para regresarte si te alejaste moviendo el mapa.
  // Devuelve la función que retira el listener del tope de zoom (o undefined si no encuadró nada).
  const encuadrar = useCallback(() => {
    if (!mapObj || !window.google || (markers.length === 0 && !userPos)) return undefined;
    try {
      const { maps } = window.google;
      const bounds = new maps.LatLngBounds();
      if (userPos) {
        const conDist = markers.filter(m => typeof m.dist === 'number');
        // Los pines todavía son los de "mejor calificados": esperar a que se reordenen por distancia.
        if (markers.length && !conDist.length) return undefined;
        const cercanos = conDist.filter(m => m.dist <= RADIO_CERCA_KM);
        bounds.extend(userPos);
        (cercanos.length ? cercanos : conDist.slice(0, 1)).forEach(m => bounds.extend(m.coords));
      } else {
        markers.forEach(m => bounds.extend(m.coords));
      }
      mapObj.fitBounds(bounds, 48);
      // Si el encuadre quedó demasiado cerca (un solo punto), limitar el zoom. El listener se
      // retira pronto para no pisar después un zoom manual del usuario.
      const listener = maps.event.addListenerOnce(mapObj, 'idle', () => {
        if (mapObj.getZoom() > ZOOM_MAX_ENCUADRE) mapObj.setZoom(ZOOM_MAX_ENCUADRE);
      });
      const retiro = setTimeout(() => maps.event.removeListener(listener), 1500);
      return () => { clearTimeout(retiro); maps.event.removeListener(listener); };
    } catch (e) { return undefined; }
  }, [markers, userPos, mapObj]);

  useEffect(() => encuadrar(), [encuadrar]);

  // Etiqueta del modo: si no hay servicios dentro del radio, dice a qué distancia está el más cercano.
  const conDistancia = userPos ? markers.filter(m => typeof m.dist === 'number') : [];
  const hayCercanos = conDistancia.some(m => m.dist <= RADIO_CERCA_KM);
  // Mientras el navegador contesta (p. ej. el cuadro de permiso abierto) no se muestra "Mejor calificados",
  // que daba a entender que no hay ubicación.
  const buscandoTuUbicacion = buscandoUbicacion && !userPos && estadoPermiso !== 'denied';
  const etiquetaModo = mode === 'near'
    ? (hayCercanos || !conDistancia.length
      ? 'Cerca de ti'
      : `El más cercano está a ${Math.round(conDistancia[0].dist)} km`)
    : (buscandoTuUbicacion ? 'Buscando tu ubicación…' : 'Mejor calificados');
  // El chip es un botón cuando hay algo que hacer: con tu ubicación te regresa a "cerca de ti" y sin ella (si
  // falló o no se contestó) la vuelve a pedir. Si el navegador la tiene bloqueada solo explica qué hacer.
  const puedeVolver = mode === 'near' && Boolean(userPos);
  const puedeReintentar = !userPos && !buscandoUbicacion && estadoPermiso !== 'denied' && Boolean(navigator.geolocation);
  const ayudaChip = puedeVolver
    ? 'Volver a mi ubicación'
    : (puedeReintentar
      ? 'Usar mi ubicación para ver lo cercano a ti'
      : (!userPos && estadoPermiso === 'denied' ? 'Permite la ubicación en tu navegador para ver lo cercano a ti' : ''));
  const chipModo = (
    <Chip
      icon={mode === 'near' || buscandoTuUbicacion
        ? <NearMeIcon style={{ color: 'white', fontSize: 16 }} />
        : <StarIcon style={{ color: 'white', fontSize: 16 }} />}
      label={etiquetaModo}
      size="small"
      onClick={puedeVolver ? encuadrar : (puedeReintentar ? pedirUbicacion : undefined)}
      sx={{
        position: 'absolute', top: 10, left: 10, bgcolor: 'primary.main', color: 'white',
        fontWeight: 700, boxShadow: 1, '& .MuiChip-icon': { color: 'white' },
        ...((puedeVolver || puedeReintentar) && { '&:hover, &.Mui-focusVisible': { bgcolor: 'primary.dark' } })
      }}
    />
  );

  const fmtPrecio = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? dinero(n) : '';
  };

  return (
    <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', mb: 4, border: 1, borderColor: 'sutil' }}>
      {mapsReady ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={userPos || DEFAULT_CENTER}
          zoom={userPos ? 12 : 5}
          onLoad={setMapObj}
          onUnmount={() => setMapObj(null)}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {/* Marcador de la ubicación del usuario */}
          {userPos && (
            <OverlayView position={userPos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div style={{ transform: 'translate(-50%, -50%)' }}>
                <MyLocationIcon style={{ color: palette.secondary.main, fontSize: 26, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
              </div>
            </OverlayView>
          )}

          {markers.map((m, i) => (
            <OverlayView key={i} position={m.coords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div
                style={{ transform: 'translate(-50%, -100%)', cursor: 'pointer', position: 'relative' }}
                onClick={() => setActive(active === i ? null : i)}
              >
                {/* Pin con la foto del proveedor (primer servicio del grupo) */}
                <div style={{ width: 46, height: 53, position: 'relative' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', border: `3px solid ${palette.primary.main}`,
                    background: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.35)', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {m.items[0]?.authorAvatarUrl
                      ? <img src={m.items[0].authorAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <PersonIcon style={{ color: palette.secondary.main, fontSize: 26 }} />}
                  </div>
                  <div style={{
                    position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                    width: 0, height: 0, borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent', borderTop: `9px solid ${palette.primary.main}`
                  }} />
                  {m.items.length > 1 && (
                    <div style={{
                      position: 'absolute', top: -5, right: -5, background: palette.error.oscuro, color: 'white',
                      borderRadius: '50%', minWidth: 18, height: 18, fontSize: 11, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 4px', border: '2px solid white', boxSizing: 'border-box'
                    }}>{m.items.length}</div>
                  )}
                </div>
                {active === i && (
                  <div style={{
                    position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)',
                    background: 'white', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    padding: '10px 12px', width: 220, zIndex: 1000
                  }}>
                    {m.items.slice(0, 4).map(s => (
                      <Typography
                        key={s._id}
                        variant="caption"
                        onClick={(e) => { e.stopPropagation(); navigate(`/servicio/${s.userId}/${s._id}`); }}
                        sx={{
                          display: 'block', fontWeight: 700, color: 'primary.main', cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {s.nombre}{fmtPrecio(s.costoServicio) && ` — ${fmtPrecio(s.costoServicio)}`}
                      </Typography>
                    ))}
                    {m.items.length > 4 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        +{m.items.length - 4} servicio(s) más
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      {m.direccion}
                      {typeof m.dist === 'number' && ` · a ${m.dist < 1 ? `${Math.round(m.dist * 1000)} m` : `${m.dist.toFixed(1)} km`}`}
                    </Typography>
                  </div>
                )}
              </div>
            </OverlayView>
          ))}
        </GoogleMap>
      ) : (
        <Box sx={{ height: ALTO_MAPA, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
          <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
        </Box>
      )}

      {/* Indicador del modo activo. Con tu ubicación es un botón: si te alejaste moviendo el mapa,
          un clic te regresa a "cerca de ti". */}
      {mapsReady && markers.length > 0 && (ayudaChip
        ? <Tooltip title={ayudaChip} describeChild>{chipModo}</Tooltip>
        : chipModo)}

      {loading && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', borderRadius: '50%', p: 0.6, boxShadow: 1 }}>
          <CircularProgress size={18} sx={{ color: 'secondary.main' }} />
        </Box>
      )}

      {mapsReady && !loading && markers.length === 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.75)', pointerEvents: 'none', textAlign: 'center', px: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            Aún no hay servicios con dirección para mostrar en el mapa.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HomeMap;
